import React, { useState, useEffect, useRef } from 'react';
import { Image, ImageProps, Spinner } from 'react-bootstrap';
import axiosInstance from 'utils/axiosInterceptors';
import { useImageRevision } from 'utils/imageRevisions';

interface RetryImageProps extends Omit<ImageProps, 'src'> {
  googleFileId: string;
  maxRetries?: number;
  retryDelay?: number;
  revision?: number;
}

const driveOriginalUrl = (googleFileId: string) =>
  `https://drive.google.com/uc?export=view&id=${googleFileId}`;

const isUsableImageBlob = (data: Blob) => {
  if (!data || data.size === 0) return false;
  // Auth/error payloads often come back as JSON blobs when responseType is 'blob'
  if (data.type === 'application/json') return false;
  return true;
};

export const RetryImage: React.FC<RetryImageProps> = ({
  googleFileId,
  maxRetries = 3,
  retryDelay = 1000,
  revision = 0,
  style,
  ...imageProps
}) => {
  const storedRevision = useImageRevision(googleFileId);
  const [isLoading, setIsLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const objectUrlRef = useRef<string | null>(null);
  const usingDriveFallbackRef = useRef(false);

  useEffect(() => {
    if (!googleFileId) {
      setIsLoading(false);
      setImageUrl('');
      return;
    }

    let cancelled = false;
    usingDriveFallbackRef.current = false;

    const revokeObjectUrl = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };

    const applyDriveFallback = () => {
      revokeObjectUrl();
      usingDriveFallbackRef.current = true;
      setImageUrl(driveOriginalUrl(googleFileId));
      setIsLoading(false);
    };

    const fetchWithAuth = async () => {
      setIsLoading(true);
      revokeObjectUrl();
      setImageUrl('');

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (cancelled) return;

        try {
          // Authenticated API first; generic images endpoint works for any Drive file id.
          const endpoints = [
            `/api/v1/images/${googleFileId}`,
            `/api/v1/vehicles/images/${googleFileId}`,
            `/api/v1/products/images/${googleFileId}`
          ];

          let blob: Blob | null = null;
          let lastError: unknown;

          for (const endpoint of endpoints) {
            try {
              const response = await axiosInstance.get(endpoint, {
                responseType: 'blob',
                params: revision ? { t: revision } : undefined,
                headers: revision ? { 'Cache-Control': 'no-cache' } : undefined
              });
              if (isUsableImageBlob(response.data)) {
                blob = response.data;
                break;
              }
              lastError = new Error(`Non-image response from ${endpoint}`);
            } catch (err) {
              lastError = err;
            }
          }

          if (!blob) {
            throw lastError ?? new Error('Image fetch failed');
          }

          if (cancelled) return;

          const objectUrl = URL.createObjectURL(blob);
          objectUrlRef.current = objectUrl;
          setImageUrl(objectUrl);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error(
            `Error fetching image (attempt ${attempt + 1}/${maxRetries + 1}):`,
            googleFileId,
            error
          );

          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          }
        }
      }

      if (!cancelled) {
        console.warn(
          `Falling back to Google Drive original URL for ${googleFileId}`
        );
        applyDriveFallback();
      }
    };

    fetchWithAuth();

    return () => {
      cancelled = true;
      revokeObjectUrl();
    };
  }, [googleFileId, maxRetries, retryDelay, revision, storedRevision]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoading(false);
    imageProps.onLoad?.(e);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Authenticated blob failed to render — try the Drive original URL once.
    if (!usingDriveFallbackRef.current && googleFileId) {
      console.warn(
        `Blob image failed to render, using Drive original URL: ${googleFileId}`
      );
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      usingDriveFallbackRef.current = true;
      setImageUrl(driveOriginalUrl(googleFileId));
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    imageProps.onError?.(e);
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa'
          }}
        >
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {imageUrl && (
        <Image
          {...imageProps}
          style={{
            ...style,
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.2s'
          }}
          src={imageUrl}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
};
